/**
 * Abstraction over network object
 * to have hex/dex/scan functions in one place
 */

import { ITicker } from "..";
import { chainIds, chainMapping } from "../enums/blockchains";
import { IMappingItem, mapping } from "../helpers/networkMapping";
import { ICommonBase } from "../interfaces/common";

class Network {
  chain: number;
  supported: boolean

  constructor(kind: number, supported: boolean) {
    this.chain = kind;
    this.supported = supported;
  }

  static createETH = (): Network =>
    new Network(chainMapping['ETH'], true);

  static fromChainId = (chainId: number): Network  => {
    if (chainIds.includes(chainId)) {
      return new Network(chainId, true);
    }
    return new Network(chainId, false);
  };

  static isValidChain = (chain: string): chain is keyof typeof chainMapping => {
    return chain in chainMapping;
  }

  static fromChain = (chain: string): Network => {
    if (this.isValidChain(chain)) {
      return this.fromChainId(chainMapping[chain]);
    }
    return new Network(chainMapping['ETH'], true);
  };

  static fromTicker = (ticker: ITicker): Network => {
    const { chain } = ticker
    return this.fromChain(chain)
  }

  /**
   * Handle case when we can't find object in mapping - return ETH
   * @param key 
   */
  getChainFromMapping = (): IMappingItem => {
    if (this.chain && mapping[this.chain]) {
      return mapping[this.chain];
    }

    return mapping[chainMapping.ETH];
  }

  getName = (): string =>
    this.getChainFromMapping().name;

  getScan = (): string =>
    this.getChainFromMapping().scan;

  getScanForWallet = (): string =>
    this.getChainFromMapping().walletScan;

  getScanForToken = (token: string): string => {
    return this.chain
      ? this.getChainFromMapping().tokenScan.split("tokenName").join(token)
      : `https://etherscan.io/token/${token}`;
  };

  // https://api.coingecko.com/api/v3/asset_platforms
  toCoingecko = (): string =>
    this.getChainFromMapping().coingecko;

  toCovalentNetwork = (): string =>
    this.getChainFromMapping().covalent;

  toTrustWallet = (): string =>
    this.getChainFromMapping().trustwallet;

  toHex = (): string => 
    this.getChainFromMapping().hex;

  /**
   * We may not have object in mapping, but we always should return a number
   * @returns decimal id
   */
  toDecimal = () =>
    this.getChainFromMapping().id;

  /**
   * Support for short symbol, used in UI like icons mapping
   * @returns
   */
  getSymbol = (): string =>
    this.getChainFromMapping().symbol;

  /**
   * Return short symbol to match metamask
   * @returns
   */
  getNativeCurrencySymbol = (): string =>
    this.getChainFromMapping().nativeCurrency;
  /**
   * Support for default RPC
   * @returns
   */
  getRPC = (): string =>
    this.getChainFromMapping().rpc;

  getCommonBases = (): ICommonBase[] =>
    this.getChainFromMapping().commonBases || [];

  getTitle = (): string =>
    this.getChainFromMapping().title;

  getSlug = (): string =>
    this.getChainFromMapping().slug;

  getImage = (): string | undefined =>
    this.getChainFromMapping().image || "";

  isETH = () => this.chain === chainMapping.ETH;

  isBSC = () => this.chain === chainMapping.BSC;

  isBSCTestnet = () => this.chain === chainMapping.BSC_TESTNET;

  isHECO = () => this.chain === chainMapping.HECO;

  isELROND = () => this.chain === chainMapping.ELROND;

  isAVAX = () => this.chain === chainMapping.AVAX;

  isMETER = () => this.chain === chainMapping.METER;

  isFTM = () => this.chain === chainMapping.FTM;

  isBTTC = () => this.chain === chainMapping.BTTC;

  isTron = () => this.chain === chainMapping.TRON;

  isPOLYGON = () => this.chain === chainMapping.POLYGON;

  isOPTIMISM = () => this.chain === chainMapping.OPTIMISM;

  isOKX = () => this.chain === chainMapping.OKX;

  isARBITRUM = () => this.chain === chainMapping.ARBITRUM;
  
  isARBITRUM_TESTNET = () => this.chain === chainMapping.ARBITRUM_SEPOLIA_TESTNET;

  isMOONBEAM = () => this.chain === chainMapping.MOONBEAM;
  
  isCRONOS = () => this.chain === chainMapping.CRONOS;
  
  isZKSYNC= () => this.chain === chainMapping.ZKSYNC;
  
  isHARMONY = () => this.chain === chainMapping.HARMONY;

  isKAVA = () => this.chain === chainMapping.KAVA;

  isCANTO = () => this.chain === chainMapping.CANTO;

  isPULSECHAIN = () => this.chain === chainMapping.PULSECHAIN;

  isBASE = () => this.chain === chainMapping.BASE;

  isARTHERA = () => this.chain === chainMapping.ARTHERA;

  isARTHERA_TESTNET = () => this.chain === chainMapping.ARTHERA_TESTNET;

  isTON = () => this.chain === chainMapping.TON;

  isMANTA = () => this.chain === chainMapping.MANTA;

  isMETIS = () => this.chain === chainMapping.METIS;

  isCONFLUX = () => this.chain === chainMapping.CONFLUX;

  isSOLANA = () => this.chain === chainMapping.SOLANA;
}

export default Network;
